/**
 * BottomNav — persistent fixed bottom tab bar shown on all inner pages.
 * Hidden on Home, Scanning/Loading, and Recipe detail pages.
 * Tabs: Meals | Flyer | Pantry | Weekly | My List
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { Tag, ChefHat, CalendarDays, ShoppingCart, UtensilsCrossed } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { useAppStore } from '@/hooks/useAppStore';

const HIDDEN_ON = ['/', '', '/scanning', '/stores'];

export default function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { cartMeals, mealPlan } = useAppStore();

  // Hide on home, scanning, stores, and recipe detail
  if (
    HIDDEN_ON.includes(pathname) ||
    pathname.startsWith('/recipe/')
  ) return null;

  const cartCount = cartMeals.length;

  const tabs = [
    {
      id: 'meals',
      label: 'Meals',
      icon: UtensilsCrossed,
      path: ROUTE_PATHS.MEAL_PLAN,
      emoji: '🍽️',
    },
    {
      id: 'flyer',
      label: 'This Week\'s Flyer',
      icon: Tag,
      path: ROUTE_PATHS.FLYER,
      emoji: '🏷️',
      accent: true,
    },
    {
      id: 'pantry',
      label: 'My Pantry',
      icon: ChefHat,
      path: ROUTE_PATHS.MY_PANTRY,
      emoji: '🧑‍🍳',
    },
    {
      id: 'weekly',
      label: 'Weekly Plan',
      icon: CalendarDays,
      path: ROUTE_PATHS.CALENDAR,
      emoji: '📅',
    },
    {
      id: 'list',
      label: 'My List',
      icon: ShoppingCart,
      path: ROUTE_PATHS.GROCERY,
      emoji: '🛒',
      badge: cartCount > 0 ? cartCount : null,
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/98 backdrop-blur border-t border-border/60 safe-area-pb">
      <div className="flex items-stretch h-16 max-w-xl mx-auto px-1">
        {tabs.map(tab => {
          const isActive = pathname === tab.path;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-colors ${
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {/* Active indicator */}
              {isActive && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-primary" />
              )}

              {/* Icon with optional badge */}
              <span className="relative">
                {tab.accent && !isActive ? (
                  <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-orange-500/15 border border-orange-500/25">
                    <Icon className="w-4 h-4 text-orange-400" />
                  </span>
                ) : (
                  <Icon className={`w-5 h-5 ${isActive ? 'text-primary' : ''}`} />
                )}
                {tab.badge && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-primary text-primary-foreground text-[10px] font-black rounded-full flex items-center justify-center px-1 leading-none">
                    {tab.badge}
                  </span>
                )}
              </span>

              {/* Label */}
              <span className={`text-[10px] font-semibold leading-none ${
                tab.id === 'flyer' && !isActive ? 'text-orange-400' : ''
              }`}>
                {tab.id === 'flyer' ? 'Flyer' : tab.label.split(' ')[tab.label.split(' ').length > 1 ? 1 : 0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
